import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlertCircle, CheckCircle2, Loader2, Save } from "lucide-react";
import { useAppSelector } from "../../../redux/hooks";
import { IDSwitch } from "./IDSwitch";

import { InvalidReasonDisplay } from "../../vault/ui/InvalidReasonDisplay";
import { CancelToReview } from "../CancelToReview";
import {
  FormValue,
  Status,
  Type,
} from "../../../redux/interfaces/folder.types";
import { DocumentExamples } from "./DocumentExamples";
import { IDTypeSelector } from "../ui/IDTypeSelector";
import { labelIDOptions } from "./fixtures/IDTypes";
import { toast } from "sonner";
import { useUploadIdFiles } from "../../../hooks/vault/mutations/useUploadIdFiles";
import { useValidatePoaByIdentity } from "../../../hooks/vault/mutations/useValidatePoaByIdentity";
import { useInvalidateVaultFolder } from "../../../hooks/vault/queries/useVaultFolder";
import { useSingleFlight } from "../../../hooks/useSingleFlight";
import type { UploadFile } from "antd/es/upload/interface";
// ID Validation Flow Components
import {
  useIDValidationFlow,
  IDValidationLoader,
  AddressConfirmationForm,
  IDValidationErrorBoundary,
  RejectedState,
  DUMMY_INE_DATA,
} from "./id-validation";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import {
  createDocumentMetadataState,
  DocumentMetadataFields,
  mapDocumentMetadataFormValues,
  validateDocumentMetadata,
  type DocumentMetadataState,
} from "./DocumentMetadataFields";
import { useLocation } from "react-router-dom";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import { Button } from "../../ui/button";
import { VaultFormValidationError } from "../../../exceptions/VaultFormValidationError";
import { summarizeVaultFormValidationError } from "../../../utils/vault/vaultFormValidationErrorSummary";
import { getDocumentUploadPolicy } from "../../../constants/uploadPolicies";
import {
  isIdentityValidationActivityProcessing,
  type IdentityValidationActivity,
  type IdentityValidationActivityUpdate,
} from "../identityValidationActivity";

interface Props {
  type: "tenant" | "guarantor" | "legal_rep" | "landlord" | "owner_rep";
  onIDValidationComplete?: (isComplete: boolean) => void;
  onIDValidationActivityChange?: (
    update: IdentityValidationActivityUpdate,
  ) => void;
  identityValidationActivity?: IdentityValidationActivity;
  isIDCompleted?: boolean;
  onNavigateToProofOfAddress?: () => void;
  onNavigateToDataValidation?: () => boolean;
  onRegisterSubmit?: (submitFn: () => Promise<boolean>) => void;
  onSetNavigationHidden?: (hidden: boolean) => void;
  onAddressConfirmationResult?: (isUpdated: boolean) => void;
  reviewAddressOnEntry?: boolean;
}

export const ID: React.FC<Props> = ({
  type,
  onIDValidationComplete,
  onIDValidationActivityChange,
  identityValidationActivity = null,
  isIDCompleted = true,
  onNavigateToProofOfAddress,
  onNavigateToDataValidation,
  onRegisterSubmit,
  onSetNavigationHidden,
  onAddressConfirmationResult,
  reviewAddressOnEntry = false,
}) => {
  const [documentType, setDocumentType] = useState<string | undefined>("ine");
  const [frontFiles, setFrontFiles] = useState<UploadFile[]>([]);
  const [backFiles, setBackFiles] = useState<UploadFile[]>([]);
  const [metadataState, setMetadataState] = useState<DocumentMetadataState>({
    values: {},
    requiredFields: {},
    displayFields: {},
    labelFields: {},
  });
  const [metadataErrors, setMetadataErrors] = useState<Record<string, string>>(
    {},
  );
  // Covers the gap between upload completion and START_VALIDATION. A folder
  // refetch can publish workflow_step="validating" in that gap; restoring it
  // must not reset the local validation request that this component owns.
  const validationSubmissionInFlightRef = useRef(false);

  const label = labelIDOptions[type];
  const location = useLocation();
  const isAdminPanel = !location.pathname.includes("vault");

  const { activeRequirement, activeVault } = useAppSelector(
    (state) => state.vault,
  );
  const uploadPolicy = getDocumentUploadPolicy({
    requirementSlug: activeRequirement?.slug,
    documentType,
    requirementStatus: activeRequirement?.status,
  });

  const { isMobile } = useBreakpoint();
  // Keep this stable so initIdFormValues and the reset effect only change when
  // the backend form definition actually changes.
  // On the admin panel we keep `document_number` so admins can view/edit it;
  // on the renter vault the field is captured by the OCR flow and hidden.
  const metadataFields = useMemo(
    () =>
      (activeRequirement?.form_values || []).filter(
        (field) =>
          field.key !== "document_type" &&
          (isAdminPanel || field.key !== "document_number"),
      ),
    [activeRequirement?.form_values, isAdminPanel],
  );

  const matchesRequirementSubfolder = useCallback(
    (
      subfolder: { id?: string; name?: string; slug?: string } | undefined,
      requirementSubfolder: string | undefined,
    ) => {
      if (!subfolder || !requirementSubfolder) return false;

      return [subfolder.id, subfolder.name, subfolder.slug].some(
        (value) => value === requirementSubfolder,
      );
    },
    [],
  );

  // Detect whether a POA requirement exists in the same subfolder
  const hasProofOfAddress = useMemo(() => {
    if (!activeVault?.subfolders || !activeRequirement?.subfolder) return false;

    return activeVault.subfolders
      .filter((subfolder) =>
        matchesRequirementSubfolder(subfolder, activeRequirement.subfolder),
      )
      .some((subfolder) =>
        (subfolder.requirements || []).some(
          (requirement) => requirement.type === Type.ProofOfAddress,
        ),
      );
  }, [
    activeRequirement?.subfolder,
    activeVault?.subfolders,
    matchesRequirementSubfolder,
  ]);

  // Mutations & invalidation
  const uploadIdFilesMutation = useUploadIdFiles(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);
  const validatePoaMutation = useValidatePoaByIdentity();
  const invalidateVaultFolder = useInvalidateVaultFolder(activeVault?.id);

  // ID Validation Flow Hook
  const validationFlow = useIDValidationFlow();
  const {
    state: validationState,
    startValidation,
    setAddressUpdated,
    resetFlow,
    initializeFromRequirement,
    isValidating,
    showAddressCheck,
    isCompleted,
    isRejected,
    shouldNavigateToProofOfAddress,
    shouldNavigateToDataValidation,
    hasError,
    error: validationError,
    invalidReason,
  } = validationFlow;

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid ||
    identityValidationActivity === "failed";
  const { isPending: isConfirmingAddress, run: runAddressConfirmation } =
    useSingleFlight();
  const showDocumentMetadata = isAdminPanel;
  const canEditDocumentMetadata = isAdminPanel || enabledInputs || isRejected;
  const isAdminSubmitting =
    uploadIdFilesMutation.isPending || updateFormValuesMutation.isPending;
  const adminActionLabel = "Actualizar";

  const requiresBothSides = [
    "ine",
    "driver_license",
    "migratory_form",
  ].includes(documentType || "");

  const documentExamples = [
    { path: "/documents/ine.jpg", description: "INE" },
    { path: "/documents/pasaporte.jpeg", description: "Pasaporte" },
    {
      path: "/documents/licencia-conducir.jpeg",
      description: "Licencia de conducir",
    },
    {
      path: "/documents/forma_migratoria.jpeg",
      description: "Forma migratoria",
    },
  ];

  // Resetear flags cuando cambia el requirement
  useEffect(() => {
    resetFlow();
  }, [activeRequirement?.id, resetFlow]);

  // Initialize validation state from requirement status AND workflow_step on load/change
  // This handles page reloads when document is already in pending_review, invalid, or mid-flow state
  useEffect(() => {
    if (activeRequirement && !validationSubmissionInFlightRef.current) {
      initializeFromRequirement({
        id: activeRequirement.id,
        status: activeRequirement.status,
        invalid_reason: activeRequirement.invalid_reason,
        workflow_step: activeRequirement.workflow_step,
        verification_data: activeRequirement.verification_data,
        hasProofOfAddress,
        documentType:
          activeRequirement.form_values?.find(
            (field) => field.key === "document_type",
          )?.value ||
          activeRequirement.verification_data?.document_type ||
          undefined,
        reviewAddressOnEntry,
      });
    }
  }, [
    activeRequirement,
    initializeFromRequirement,
    hasProofOfAddress,
    reviewAddressOnEntry,
  ]);

  const initIdFormValues = useCallback(() => {
    if (!activeRequirement?.form_values) return;

    // For fresh requirements (PendingUpload), always default to INE
    // since the backend form_values may have a different initial value.
    // Only restore saved document type after user has already uploaded.
    if (activeRequirement.status === Status.PendingUpload) {
      setDocumentType("ine");
      return;
    }

    const values = activeRequirement.form_values || [];
    const documentTypeValue =
      values.find((item: FormValue) => item.key === "document_type")?.value ||
      "ine";

    setDocumentType(documentTypeValue);
    setMetadataState(createDocumentMetadataState(metadataFields));
    setMetadataErrors({});
  }, [
    activeRequirement?.form_values,
    activeRequirement?.status,
    metadataFields,
  ]);

  useEffect(() => {
    // Reset files when requirement changes (different tab/requirement).
    // Skip during active validation OR pending upload mutation — vault refetches
    // can trigger this effect and initIdFormValues would overwrite documentType
    // back to "ine". The upload mutation fires before startValidation sets
    // isValidating, so we must also check the mutation's pending state.
    if (isValidating || uploadIdFilesMutation.isPending) return;
    setFrontFiles([]);
    setBackFiles([]);
    initIdFormValues();
  }, [
    activeRequirement?.id,
    initIdFormValues,
    isValidating,
    uploadIdFilesMutation.isPending,
  ]);

  // Clear file state when a requirement is rejected (Invalid) so the user must select new files
  useEffect(() => {
    if (activeRequirement?.status === Status.Invalid) {
      setFrontFiles([]);
      setBackFiles([]);
    }
  }, [activeRequirement?.status]);

  // Notify parent when ID validation completes
  useEffect(() => {
    if (onIDValidationComplete) {
      onIDValidationComplete(isCompleted);
    }
  }, [isCompleted, onIDValidationComplete]);

  // Navigate to proof of address when user says address is NOT updated
  useEffect(() => {
    if (
      shouldNavigateToProofOfAddress &&
      identityValidationActivity === null &&
      isIDCompleted &&
      onNavigateToProofOfAddress
    ) {
      // Small delay to allow the upload to complete first
      const timer = setTimeout(() => {
        onNavigateToProofOfAddress();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    shouldNavigateToProofOfAddress,
    identityValidationActivity,
    isIDCompleted,
    onNavigateToProofOfAddress,
  ]);

  // Clear uploaded files when document type changes
  const handleDocumentTypeChange = (newType: string) => {
    if (newType !== documentType) {
      setFrontFiles([]);
      setBackFiles([]);
      resetFlow();
    }
    setDocumentType(newType);
  };

  const handleMetadataChange = useCallback(
    (key: string, value: string | undefined | null) => {
      setMetadataState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          [key]: value,
        },
      }));
      setMetadataErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const handleMetadataBlur = useCallback(
    async (key: string) => {
      const errors = await validateDocumentMetadata(
        metadataFields,
        metadataState,
      );
      setMetadataErrors((prev) => ({
        ...prev,
        [key]: errors[key],
      }));
    },
    [metadataFields, metadataState],
  );

  const buildDocumentFormValues = useCallback(() => {
    if (!activeRequirement?.form_values) return null;

    return mapDocumentMetadataFormValues(
      activeRequirement.form_values.map((formItem) =>
        formItem.key === "document_type"
          ? { ...formItem, value: documentType ?? formItem.value ?? "" }
          : formItem,
      ),
      metadataState.values,
    );
  }, [activeRequirement?.form_values, documentType, metadataState.values]);

  // Check if we have all required files
  const hasRequiredFiles = requiresBothSides
    ? frontFiles.length > 0 && backFiles.length > 0
    : frontFiles.length > 0;
  const hasAnySelectedFiles = frontFiles.length > 0 || backFiles.length > 0;

  // Handle submission - upload files to storage and start validation flow
  // Returns true if validation started successfully, false if not ready
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    let submissionClaimed = false;
    let validationStarted = false;
    let finalActivity: IdentityValidationActivity = "failed";

    try {
      if (
        validationSubmissionInFlightRef.current ||
        isIdentityValidationActivityProcessing(identityValidationActivity)
      ) {
        return false;
      }
      validationSubmissionInFlightRef.current = true;
      submissionClaimed = true;

      const nextMetadataErrors = showDocumentMetadata
        ? await validateDocumentMetadata(metadataFields, metadataState)
        : {};
      setMetadataErrors(nextMetadataErrors);
      if (Object.keys(nextMetadataErrors).length > 0) {
        toast.error("Por favor revisa los datos del documento");
        return false;
      }

      const newFormValues = buildDocumentFormValues();

      // Admin panel preserves the legacy reviewer flow:
      // update metadata with staff credentials and optionally replace files,
      // without running the renter OCR flow or changing the current status.
      if (isAdminPanel) {
        if (!activeRequirement || !newFormValues) {
          toast.error("No se pudieron preparar los datos del documento");
          return false;
        }

        if (hasAnySelectedFiles && !documentType) {
          toast.error("Por favor selecciona un tipo de identificación.");
          return false;
        }

        if (hasAnySelectedFiles && !hasRequiredFiles) {
          toast.error("Por favor sube los archivos requeridos");
          return false;
        }

        if (hasRequiredFiles) {
          const frontFile = frontFiles[0]?.originFileObj;
          if (!frontFile) {
            toast.error("Error: No se pudo obtener el archivo del frente");
            return false;
          }

          const needsBack = [
            "ine",
            "driver_license",
            "migratory_form",
          ].includes(documentType || "");
          const backFile = needsBack ? backFiles[0]?.originFileObj : undefined;
          if (needsBack && !backFile) {
            toast.error("Error: No se pudo obtener el archivo del reverso");
            return false;
          }

          const formData = new FormData();
          formData.append("files", frontFile);
          if (needsBack && backFile) {
            formData.append("files", backFile);
          }
          formData.append("document_type", documentType!);

          await uploadIdFilesMutation.mutateAsync({
            reqId: activeRequirement.id,
            formData,
            uploadPolicy,
            newFormValues,
            // Replacing evidence in the reviewer flow is atomic: do not
            // downgrade an already approved requirement before upload succeeds.
            newStatus: activeRequirement.status,
            uploadBatchAction: "replace_evidence",
          });

          setFrontFiles([]);
          setBackFiles([]);
        } else {
          await updateFormValuesMutation.mutateAsync({
            reqId: activeRequirement.id,
            form_values: newFormValues,
            status: activeRequirement.status,
          });
        }
        toast.success("Datos del documento actualizados correctamente.");
        return true;
      }

      if (!documentType || !hasRequiredFiles) {
        toast.error("Por favor sube los archivos requeridos");
        return false;
      }

      // Get the actual File objects from the upload files with proper null checks
      const frontFile = frontFiles[0]?.originFileObj;
      if (!frontFile) {
        toast.error("Error: No se pudo obtener el archivo del frente");
        return false;
      }

      const needsBack = ["ine", "driver_license", "migratory_form"].includes(
        documentType,
      );
      const backFile = needsBack ? backFiles[0]?.originFileObj : undefined;
      if (needsBack && !backFile) {
        toast.error("Error: No se pudo obtener el archivo del reverso");
        return false;
      }

      // Step 1: Upload files to document storage (GCS)
      const formData = new FormData();
      formData.append("files", frontFile);
      if (needsBack && backFile) {
        formData.append("files", backFile);
      }
      formData.append("document_type", documentType);

      validationStarted = true;
      onIDValidationActivityChange?.({
        requirementId: activeRequirement!.id,
        activity: "uploading",
      });
      await uploadIdFilesMutation.mutateAsync({
        reqId: activeRequirement!.id,
        formData,
        uploadPolicy,
        newFormValues: newFormValues ?? undefined,
        // A renter retry replaces the previous identity evidence atomically
        // when the requirement PATCH commits. This action must not be sent to
        // the multipart/resumable upload initiation endpoint.
        uploadBatchActionOnSubmit: "replace_evidence_on_submit",
      });

      // Step 3: Start validation flow (OCR will set the final status)
      onIDValidationActivityChange?.({
        requirementId: activeRequirement!.id,
        activity: "validating",
      });
      const response = await startValidation(
        documentType,
        undefined,
        undefined,
        activeRequirement?.id,
        hasProofOfAddress,
      );

      if (!response) {
        // startValidation owns the visible error state. Keep the submit
        // result false so the parent cannot navigate based on the temporary
        // pending_review status written while binding the upload batch.
        if (activeVault?.id) {
          await invalidateVaultFolder();
        }
        return false;
      }

      // Refresh the vault to update sidebar with new requirement status
      if (activeVault?.id) {
        // Do not let the parent navigate while the cached requirement still
        // contains the transient pending_review/validating upload state.
        // Waiting for the active refetch keeps the sidebar and route guards in
        // sync with the terminal workflow step written by verification.
        await invalidateVaultFolder({ throwOnError: true });
      }

      const isAcceptedResult =
        response.success === true &&
        (response.status === "valid" || response.status === "pending_review");
      if (!isAcceptedResult) {
        if (response.status === "invalid") {
          setFrontFiles([]);
          setBackFiles([]);
        }
        return false;
      }

      setFrontFiles([]);
      setBackFiles([]);
      finalActivity = null;
      return true;
    } catch (error: unknown) {
      if (error instanceof VaultFormValidationError) {
        const summary = summarizeVaultFormValidationError(
          error,
          metadataFields,
        );
        setMetadataErrors((prev) => ({ ...prev, ...summary.fields }));
        if (summary.description) {
          toast.error(error.message, { description: summary.description });
        } else {
          toast.error(error.message);
        }
      } else {
        // Non-validation failures (upload network errors, coded backend
        // errors thrown as plain Error) reach here without any user feedback.
        // The mutation is already logged globally (queryClient MutationCache
        // onError), so surface a toast — do NOT re-throw: the renter submit
        // path runs fire-and-forget with no try/catch and would produce an
        // unhandled rejection.
        console.error("ID submit failed:", error);
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : "Ocurrió un error al enviar el documento. Inténtalo de nuevo.",
        );
      }
      return false;
    } finally {
      if (submissionClaimed) {
        validationSubmissionInFlightRef.current = false;
      }
      if (validationStarted) {
        onIDValidationActivityChange?.({
          requirementId: activeRequirement!.id,
          activity: finalActivity,
        });
      }
    }
  }, [
    activeRequirement,
    activeVault?.id,
    backFiles,
    documentType,
    frontFiles,
    hasProofOfAddress,
    hasAnySelectedFiles,
    hasRequiredFiles,
    invalidateVaultFolder,
    isAdminPanel,
    startValidation,
    uploadIdFilesMutation,
    uploadPolicy,
    updateFormValuesMutation,
    buildDocumentFormValues,
    metadataFields,
    metadataState,
    showDocumentMetadata,
    identityValidationActivity,
    onIDValidationActivityChange,
  ]);

  // Register submit function with parent so "Siguiente" button can trigger validation
  useEffect(() => {
    if (onRegisterSubmit) {
      onRegisterSubmit(handleSubmit);
    }
  }, [handleSubmit, onRegisterSubmit]);

  // Handle address confirmation response
  const handleAddressConfirmation = async (isUpdated: boolean) => {
    if (identityValidationActivity !== null) return;

    await runAddressConfirmation(async () => {
      const workflowPersisted = await setAddressUpdated(isUpdated);
      if (!workflowPersisted) {
        toast.error(
          "No se pudo guardar la confirmación de domicilio. Intenta nuevamente.",
        );
        return;
      }

      // Notify parent about address confirmation result
      if (onAddressConfirmationResult) {
        onAddressConfirmationResult(isUpdated);
      }

      const proofOfAddressReq =
        isUpdated && activeRequirement?.id && activeVault
          ? activeVault.subfolders
              ?.filter((subfolder) =>
                matchesRequirementSubfolder(
                  subfolder,
                  activeRequirement.subfolder,
                ),
              )
              .flatMap((subfolder) => subfolder.requirements || [])
              .find((requirement) => requirement.type === Type.ProofOfAddress)
          : undefined;

      // If address IS updated, auto-validate the proof of address requirement when it exists.
      if (
        isUpdated &&
        activeRequirement?.id &&
        activeVault &&
        proofOfAddressReq
      ) {
        try {
          await validatePoaMutation.mutateAsync({
            poaId: proofOfAddressReq.id,
            identityReqId: activeRequirement.id,
          });

          if (activeVault.id) {
            await invalidateVaultFolder();
          }

          toast.success("Comprobante de domicilio validado automaticamente");
        } catch (error) {
          console.error("Failed to auto-validate proof of address:", error);
          toast.error(
            "No se pudo validar automaticamente el comprobante de domicilio",
          );

          if (activeVault.id) {
            await invalidateVaultFolder();
          }

          onNavigateToProofOfAddress?.();
          return;
        }
      }

      // If address IS updated and the flow includes a virtual data-validation step, navigate there.
      if (isUpdated && proofOfAddressReq && onNavigateToDataValidation?.()) {
        return;
      }

      // ID-only flows do not have a data-validation step. Refresh the folder so the
      // sidebar/workflow_step unlocks immediately without requiring a page reload.
      if (isUpdated && activeVault?.id) {
        await invalidateVaultFolder();
        return;
      }

      // If address is NOT updated, we need to go to proof of address
      // The validation flow will mark as completed but shouldNavigateToProofOfAddress will be true
      // Note: We don't call handleFinalSubmit() here because the INE files were already
      // uploaded during the OCR validation process. We just need to refresh the folder
      // data so the sidebar updates to show proof of address as unlocked.
      if (!isUpdated && activeVault?.id) {
        // Refresh folder to update sidebar with new workflow_step status
        await invalidateVaultFolder();
      }
    });
  };

  // Render validation flow content
  const renderValidationFlow = () => {
    // Priority 1: Validating (loader) — MUST be first so re-uploads after rejection show the loader
    // Without this priority, activeRequirement?.status === Invalid from a previous attempt
    // would render RejectedState instead of the loader during re-validation
    if (isValidating) {
      return (
        <div className="w-full flex items-center justify-center py-16">
          <IDValidationLoader
            progress={validationState.validationProgress}
            documentType={documentType || ""}
          />
        </div>
      );
    }

    // Priority 2: Rejected/Invalid (<60% confidence or failed attributes, or pre-existing invalid from backend)
    // Show rejection message, then the upload form below will allow re-upload
    if (isRejected || activeRequirement?.status === Status.Invalid) {
      return (
        <RejectedState
          invalidReason={
            invalidReason || activeRequirement?.invalid_reason || null
          }
        />
      );
    }

    // Priority 3: Address check (INE only)
    if (showAddressCheck) {
      // Use extracted address or fallback to dummy data
      const addressToShow =
        validationState.extractedData?.direccion || DUMMY_INE_DATA.direccion;
      return (
        <AddressConfirmationForm
          address={addressToShow}
          onConfirm={handleAddressConfirmation}
          isSubmitting={
            isConfirmingAddress || identityValidationActivity !== null
          }
        />
      );
    }

    return null;
  };

  // Check if we're in the middle of the validation flow
  // Note: isRejected shows the RejectedState component ABOVE the upload form, not instead of it
  // Include shouldNavigateToProofOfAddress to prevent flash of upload form during navigation
  const hasLocalValidationFailure = identityValidationActivity === "failed";
  const isInValidationFlow =
    isValidating ||
    (!hasLocalValidationFailure &&
      (showAddressCheck ||
        shouldNavigateToProofOfAddress ||
        shouldNavigateToDataValidation));

  // Hide navigation footer during validation flow sub-steps that have their own actions
  useEffect(() => {
    if (onSetNavigationHidden) {
      onSetNavigationHidden(isInValidationFlow);
    }
    return () => {
      if (onSetNavigationHidden) {
        onSetNavigationHidden(false);
      }
    };
  }, [isInValidationFlow, onSetNavigationHidden]);

  // Show upload form when rejected (user can re-upload) or when not in validation flow
  const showUploadForm = !isInValidationFlow || isRejected;

  // Unified error message: rejection reason OR API error OR pre-existing invalid reason
  const errorMessage: string | null = (() => {
    if (isRejected && invalidReason) return invalidReason;
    if (hasError && validationError) return validationError;
    if (
      activeRequirement?.status === Status.Invalid &&
      activeRequirement?.invalid_reason
    ) {
      return activeRequirement.invalid_reason;
    }
    return null;
  })();

  // Render completion only after the validation state machine reached a
  // terminal step. Upload binding temporarily sets pending_review before the
  // provider responds, so status alone is not proof of successful validation.
  // Admin panel always renders the form so reviewers can inspect/edit fields like `document_number`.
  if (
    !isAdminPanel &&
    (activeRequirement?.status === Status.Valid ||
      (activeRequirement?.status === Status.PendingReview && isCompleted)) &&
    !isInValidationFlow &&
    !isRejected &&
    !hasError &&
    identityValidationActivity === null &&
    isIDCompleted
  ) {
    return (
      <div className="w-full flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center justify-center px-6 max-w-md w-full">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 ring-8 bg-emerald-50 ring-emerald-50/50">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>

          <h3 className="text-xl font-semibold text-slate-900 mb-2 text-center">
            Identificación aprobada
          </h3>
          <p className="text-sm text-slate-500 mb-8 text-center leading-relaxed">
            Tu identificación oficial ha sido verificada. Puedes continuar con
            el siguiente paso.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full relative overflow-x-hidden">
      {/* Validation Flow Overlay - wrapped in Error Boundary for graceful failure */}
      {/* Note: isRejected renders RejectedState above the upload form, not instead of it */}
      {isInValidationFlow && !isRejected && (
        <IDValidationErrorBoundary
          documentType={documentType}
          onReset={() => {
            resetFlow();
            setFrontFiles([]);
            setBackFiles([]);
          }}
        >
          {renderValidationFlow()}
        </IDValidationErrorBoundary>
      )}

      {/* Normal upload view - shown when not in blocking validation flow OR when rejected (for re-upload) */}
      {showUploadForm && (
        <>
          {/* Header - hide when rejected since RejectedState has its own header */}
          {!isRejected && activeRequirement?.status !== Status.Invalid && (
            <div className="mb-6 md:mb-8">
              <p className="text-gray-600">
                Sube una identificacion oficial vigente del{" "}
                <span className="font-semibold text-gray-900">{label}</span>.
                {requiresBothSides
                  ? " Necesitamos ambos lados del documento."
                  : " Necesitamos la pagina principal."}
              </p>
            </div>
          )}

          {/* Unified Error Display - single format for all error states */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium">Error de verificacion</span>
              </div>
              <p className="text-red-600 mt-2 text-sm">
                <InvalidReasonDisplay reason={errorMessage} />
              </p>
            </div>
          )}

          {/* Two column layout - stack on mobile, 40/60 split on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
            {/* Left Column - Document Type Selection */}
            <div className="w-full space-y-6 md:sticky md:top-0 md:self-start">
              <IDTypeSelector
                value={documentType}
                onChange={handleDocumentTypeChange}
                disabled={!enabledInputs && !isRejected}
              />

              {/* Tips - collapsible on mobile, always visible on desktop */}
              {isMobile ? (
                <details className="group rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                  <summary className="text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer min-h-[44px] flex items-center select-none list-none [&::-webkit-details-marker]:hidden">
                    <span className="flex-1">Tips de captura</span>
                    <span className="text-gray-400 text-sm transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      Superficie plana y bien iluminada
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      Las 4 esquinas deben ser visibles
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      Evita sombras y reflejos
                    </li>
                  </ul>
                  <div className="mt-3">
                    <DocumentExamples urls={documentExamples} />
                  </div>
                </details>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Tips de captura
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      Superficie plana y bien iluminada
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      Las 4 esquinas deben ser visibles
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      Evita sombras y reflejos
                    </li>
                  </ul>
                  <DocumentExamples urls={documentExamples} />
                </div>
              )}
            </div>

            {/* Right Column - File Upload */}
            <div className="w-full space-y-6">
              {showDocumentMetadata && metadataFields.length > 0 && (
                <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 sm:p-5">
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Datos del documento
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Completa los datos manuales del documento antes de
                      continuar.
                    </p>
                  </div>
                  <DocumentMetadataFields
                    fields={metadataFields}
                    state={metadataState}
                    errors={metadataErrors}
                    onChange={handleMetadataChange}
                    onBlur={handleMetadataBlur}
                    disabled={!canEditDocumentMetadata}
                  />
                </div>
              )}

              {documentType && (
                <IDSwitch
                  documentType={documentType}
                  onFrontChange={setFrontFiles}
                  onBackChange={setBackFiles}
                  frontFiles={frontFiles}
                  backFiles={backFiles}
                />
              )}

              {/* Cancel to Review - only show when pending review AND not OCR-triggered */}
              {activeRequirement?.status === Status.PendingReview &&
                !activeRequirement?.verification_data && (
                  <CancelToReview requirement={activeRequirement} />
                )}

              {isAdminPanel && (
                <div className="flex justify-start">
                  <Button
                    type="button"
                    onClick={() => {
                      void handleSubmit();
                    }}
                    disabled={isAdminSubmitting}
                    className="min-w-28"
                  >
                    {isAdminSubmitting ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Save />
                    )}
                    {adminActionLabel}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
